/**
 * Paths with their ids and capability tokens taken out, for anything that
 * leaves the browser for a vendor: Sentry span names and metric labels, and
 * the page URLs PostHog records.
 *
 * Two reasons, and they need different amounts of scrubbing:
 *
 *  - CARDINALITY. A metric labelled with the raw path gets one series per
 *    invoice, per visit, per customer; api.response_time stops meaning
 *    anything and Sentry's quota goes on noise. For Sentry every uuid and
 *    number becomes `:id` and the query string goes (normalizeApiPath).
 *  - CAPABILITY TOKENS. The public pages are opened by a token in the path
 *    (/pay/:token, /w9/:token, /t/:token…), and that token IS the access: a
 *    vendor dashboard showing it lets anyone who can read the dashboard open
 *    the customer's invoice or W-9. PostHog keeps its ids (they help to
 *    debug a funnel and open nothing) but loses the tokens (redactPathTokens).
 *
 * Token routes are matched from the start of the path, so a business screen
 * that happens to share a word (a "proposals" list) is not mistaken for one.
 */

export const ID_PLACEHOLDER = ":id";
export const TOKEN_PLACEHOLDER = ":token";

/**
 * Leading segments after which the next segment is a capability token. Web
 * pages first (App.tsx's public routes), then the API's public endpoints
 * (config/routes.rb). Longer prefixes come before the shorter ones they
 * start with (/b/cancel before /b).
 */
const TOKEN_ROUTE_PREFIXES: readonly string[] = [
  // Web pages
  "/pay",
  "/w9",
  "/t",
  "/e",
  "/r",
  "/u",
  "/f",
  "/p",
  "/b/cancel",
  "/b",
  "/book/status",
  "/book",
  "/lease-signing",
  "/my-tenancy",
  "/report-a-repair",
  "/c", // PRD 107: a customer's standing account link, the widest token of all
  "/s", // PRD 104: a completed job's feedback page
  "/card", // PRD 125: a client's card-on-file page
  // API: public, token-addressed
  "/api/v1/customer-hubs",
  "/api/v1/customer-hub-media",
  "/api/v1/outreach/unsubscribe",
  "/api/v1/lead-forms",
  "/api/v1/bookings/cancel",
  "/api/v1/bookings",
  "/api/v1/proposals",
  "/api/v1/visit-previews",
  "/api/v1/estimate-pages",
  "/api/v1/feedback-pages",
  "/api/v1/booking-pages",
  "/api/v1/booking-requests",
  "/api/v1/business/tenant-portal",
  "/api/v1/business/maintenance-reports",
  "/api/v1/business/client-requests",
  "/api/v1/business/lease-signings",
  "/api/v1/business/payment-links",
  "/api/v1/business/w9-forms",
  "/api/v1/public/card", // PRD 125: the card page's api
].map((prefix) => prefix.toLowerCase());

const PREFIX_SEGMENTS: readonly string[][] = TOKEN_ROUTE_PREFIXES.map((prefix) => prefix.split("/").filter(Boolean));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMBER = /^\d+$/;

/**
 * An opaque, random-looking segment on a route not listed above: 20 or more
 * URL-safe characters with letters and digits, and not a hyphenated slug
 * (`schedule-c-2026-preview`).
 */
const looksLikeToken = (segment: string): boolean =>
  /^[A-Za-z0-9_-]{20,}$/.test(segment)
  && /\d/.test(segment)
  && /[A-Za-z]/.test(segment)
  && (segment.match(/-/g)?.length ?? 0) < 3;

/** The index of the segment holding a token on a token route, or -1. */
const tokenIndex = (segments: string[]): number => {
  const lowered = segments.map((segment) => segment.toLowerCase());
  const match = PREFIX_SEGMENTS.find(
    (prefix) => prefix.length < lowered.length && prefix.every((segment, i) => lowered[i] === segment)
  );
  return match ? match.length : -1;
};

const splitPath = (value: string): { path: string; rest: string } => {
  const cut = value.search(/[?#]/);
  return cut === -1 ? { path: value, rest: "" } : { path: value.slice(0, cut), rest: value.slice(cut) };
};

const mapSegments = (path: string, map: (segment: string, index: number, token: number) => string): string => {
  const leading = path.startsWith("/") ? "/" : "";
  const segments = path.split("/").filter((segment, i) => !(i === 0 && segment === ""));
  const token = tokenIndex(segments);
  return leading + segments.map((segment, i) => (segment ? map(segment, i, token) : segment)).join("/");
};

/**
 * A request path fit for a span name or a metric label: query and fragment
 * dropped, uuids and numbers to `:id`, capability tokens to `:token`.
 *
 *   /api/v1/business/invoices/3f2b…/pdf?x=1  →  /api/v1/business/invoices/:id/pdf
 *   /api/v1/business/w9-forms/AbC123…        →  /api/v1/business/w9-forms/:token
 */
export const normalizeApiPath = (value: string): string => {
  const { path } = splitPath(value);
  const rooted = path.startsWith("/") ? path : `/${path}`;
  return mapSegments(rooted, (segment, i, token) => {
    if (i === token) return TOKEN_PLACEHOLDER;
    if (UUID.test(segment) || NUMBER.test(segment)) return ID_PLACEHOLDER;
    if (looksLikeToken(segment)) return TOKEN_PLACEHOLDER;
    return segment;
  });
};

/**
 * `value` (a path, or an absolute URL) with the token of a token route
 * replaced and everything else, query included, left as it was. Anything
 * that is not a path or a URL is returned unchanged.
 */
export const redactPathTokens = (value: string): string => {
  if (value.startsWith("/") && !value.startsWith("//")) {
    const { path, rest } = splitPath(value);
    const redacted = mapSegments(path, (segment, i, token) => (i === token ? TOKEN_PLACEHOLDER : segment));
    return redacted === path ? value : redacted + rest;
  }
  const absolute = /^(https?:\/\/[^/?#]+)(\/[^?#]*)?(.*)$/i.exec(value);
  if (absolute && absolute[2]) {
    const redacted = redactPathTokens(absolute[2]);
    return redacted === absolute[2] ? value : absolute[1] + redacted + absolute[3];
  }
  return value;
};

/**
 * normalizeApiPath for an absolute URL as well as a path: the origin is kept,
 * the path normalised, the query and fragment dropped. For URLs Sentry
 * records on its own (fetch spans, breadcrumbs, the page an error happened on).
 */
export const normalizeUrl = (value: string): string => {
  const absolute = /^(https?:\/\/[^/?#]+)(\/[^?#]*)?/i.exec(value);
  if (absolute) return absolute[1] + (absolute[2] ? normalizeApiPath(absolute[2]) : "");
  if (value.startsWith("/")) return normalizeApiPath(value);
  return value;
};
